import { isDisplayOnlyField, isProfileCoreId, validateIntakeAnswers } from '../intake.js';
import { answerText, isEmptyAnswer, sameAnswer } from './values.js';
/**
 * Consent at intake (spec 3.10, Phase 3): the "What you're sharing with <Agency>" review step and the one rule of
 * which answers a submission shares. PURE and shared, so the runner's review step and the server's submit agree:
 * kept = the ids the talent left on (`share`), or every answered id when the client sent no list (an old client:
 * today's behavior), plus every required id (required is never withheld).
 */
/** The agency's name as the copy says it. */
const agencyName = (agency) => agency.trim() || 'this agency';
export const SHARE_REVIEW_TITLE = (agency) => `What you're sharing with ${agencyName(agency)}`;
/**
 * What submitting does (spec 3.10), and the one rule of stopping later (spec 3.12): the talent can stop sharing ANY item
 * in Sharing, a required one too, and the agency keeps what it already has.
 */
export const SHARE_REVIEW_BODY = (agency) => {
    const name = agencyName(agency);
    return `Submitting shares these answers with ${name}. They will see updates you make to them in My info. You can stop sharing any of them later in Sharing. ${name} keeps what it already has.`;
};
/** What a submission does NOT do (spec 3.10, 3.11): nothing else is shared, and a later ask needs the talent's OK. */
export const SHARE_REVIEW_LIMITS = (agency) => `Nothing else from your profile is shared. If ${agencyName(agency)} asks for more later, it needs your OK.`;
/** The review step's first block: the row's own name, email and phone, which go with the link to the agency (no switch). */
export const SHARE_REVIEW_CORE = (agency) => `Goes with your link to ${agencyName(agency)}`;
export const REQUIRED_BY = (agency) => `Required by ${agencyName(agency)}`;
export const FROM_YOUR_PROFILE = 'From your profile';
export const STORED_SECURELY = 'Stored securely';
/** An optional answer switched off that the agency does not have: it never reaches the agency. */
export const KEPT_PRIVATE = 'Kept private';
/**
 * An optional answer switched off that the agency already has (an earlier form shared it): submitting stops sharing it,
 * and the agency keeps its copy (grants.recordIntakeConsent ends that share; spec 3.10, 3.12).
 */
export const STOPS_SHARING = (agency) => `Stops sharing. ${agencyName(agency)} keeps what it already has.`;
/** The fields a submission can share: answerable and not the row's own name, email and phone columns. */
const shareable = (f) => !isDisplayOnlyField(f.type) && !isProfileCoreId(f.id);
/**
 * The review step's rows, in two blocks, each in the form's order (spec 3.10): first the row's own name, email and phone
 * (`core`: they go with the link to the agency, so a returning talent sees exactly what goes, prefilled or typed), then
 * every other field with an answer, and every required one. `prefilled`: the ids still showing the profile's value
 * ("From your profile"). `keep`: ids whose row stays while its answer is empty (the prefilled ones: clearing one to
 * type it again must not take its input away).
 */
export function sharingReviewItems(fields, answers, prefilled, keep = new Set()) {
    const core = [];
    const rest = [];
    for (const f of fields) {
        if (isDisplayOnlyField(f.type))
            continue;
        const v = answers[f.id];
        if (isEmptyAnswer(v) && !f.required && !keep.has(f.id))
            continue;
        const sensitive = !!f.sensitive;
        const isCore = isProfileCoreId(f.id);
        (isCore ? core : rest).push({
            id: f.id,
            label: f.label,
            summary: isEmptyAnswer(v) ? '' : sensitive ? STORED_SECURELY : answerText(v),
            required: !!f.required,
            prefilled: prefilled.has(f.id),
            sensitive,
            core: isCore,
        });
    }
    return [...core, ...rest];
}
/**
 * What stops a review step's submit (spec 3.10): an answer the form refuses, such as a required one emptied with Change.
 * The server's own check (validateIntakeAnswers) over what the runner shows. Null when it can be submitted.
 */
export function reviewProblem(fields, values) {
    const checked = validateIntakeAnswers([...fields], { ...values });
    return checked.ok ? null : checked.error;
}
/**
 * The steps a returning talent is shown in the gap-only join (spec 3.10): every field the prefill did not answer, minus
 * a heading or info card whose questions the prefill answered in full (a step with nothing left to answer). A heading
 * or card with no question after it stays, as on the full form.
 */
export function gapSteps(fields, prefilled) {
    const filled = new Set(prefilled);
    const out = [];
    for (let i = 0; i < fields.length;) {
        const cards = [];
        while (i < fields.length && isDisplayOnlyField(fields[i].type))
            cards.push(fields[i++]);
        const questions = [];
        while (i < fields.length && !isDisplayOnlyField(fields[i].type))
            questions.push(fields[i++]);
        const asked = questions.filter((q) => !filled.has(q.id));
        if (asked.length || !questions.length)
            out.push(...cards);
        out.push(...asked);
    }
    return out;
}
/**
 * The ids a submission shares (spec 3.10): `share` when the client sent it (the ids left on in the review step),
 * else every answered id; plus every required id (never withheld); plus every answered id the review step never
 * lists (the row's own name, email and phone: nothing offered to switch off is nothing withheld). Only ids of the
 * form's answerable fields count.
 */
export function keptIds(fields, answers, share) {
    const known = new Set(fields.filter((f) => !isDisplayOnlyField(f.type)).map((f) => f.id));
    const kept = new Set((share ?? Object.keys(answers).filter((id) => !isEmptyAnswer(answers[id]))).filter((id) => known.has(id)));
    for (const f of fields) {
        if (!known.has(f.id))
            continue;
        if (f.required || (!shareable(f) && !isEmptyAnswer(answers[f.id])))
            kept.add(f.id);
    }
    return kept;
}
/** The answered ids a submission withholds: answered, and not kept. */
export const withheldIds = (answers, kept) => Object.keys(answers).filter((id) => !kept.has(id) && !isEmptyAnswer(answers[id]));
/** The prefilled ids still showing the value the profile gave: marked "From your profile", and never posted back. */
export function untouchedPrefill(fields, values, prefill) {
    if (!prefill)
        return new Set();
    const typeOf = new Map(fields.map((f) => [f.id, f.type]));
    return new Set(prefill.ids.filter((id) => typeOf.has(id) && !isEmptyAnswer(prefill.values[id]) && sameAnswer(values[id], prefill.values[id], typeOf.get(id))));
}
/**
 * The submit body of a runner that showed the review step (spec 3.10). `values` is what the runner shows (the
 * talent's answers over the prefill); `off` the optional items switched off. The answers are what the talent typed or
 * changed: a prefilled value left as it was is never posted back (the server merges it from the record). `share` is
 * every answered id left on, every required id (never withheld) and the row's own name, email and phone when answered
 * (shown with no switch: they go with the link). The prefill names its record and the untouched ids that are shared.
 */
export function reviewSubmitBody(fields, values, prefill, off) {
    const untouched = untouchedPrefill(fields, values, prefill);
    const share = fields
        .filter((f) => !isDisplayOnlyField(f.type))
        .filter((f) => (f.required ? true : !isEmptyAnswer(values[f.id]) && (!shareable(f) || !off.has(f.id))))
        .map((f) => f.id);
    const answers = Object.fromEntries(Object.entries(values).filter(([id]) => !untouched.has(id)));
    const prefilled = share.filter((id) => untouched.has(id));
    return prefill && prefilled.length ? { answers, share, prefill_record: prefill.record, prefilled_ids: prefilled } : { answers, share };
}
/**
 * The public runner's review decision (spec 3.10), from its run as GET /intake/:token answers it: with TALENT_CONSENT_UI
 * off the run carries nothing, so it starts as today (straight to the questions after sign-in, no review step); with it
 * on, a signed-in run first decides in the prefill phase, and staff of the form's agency (`staff`: the server's own
 * isStaffOf) never see the review step. `staffKnown` once the run was read with the session, or read again after a
 * sign-in during the run (`rereadDone`: a caller the server still cannot place then gets no review, as today).
 */
export function runReview(run, rereadDone = false) {
    const consentUi = run?.consent_ui === true;
    return {
        consentUi,
        staffKnown: typeof run?.staff === 'boolean' || rereadDone,
        review: consentUi && run?.staff === false,
        afterSignIn: consentUi ? 'prefill' : 'steps',
    };
}
//# sourceMappingURL=consent.js.map